import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, of, switchMap } from 'rxjs';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { NotificationService } from '@progress/kendo-angular-notification';
import { FileRestrictions, FileSelectModule } from '@progress/kendo-angular-upload';
import { downloadIcon, fileCsvIcon, plusIcon, SVGIcon, uploadIcon, xIcon } from '@progress/kendo-svg-icons';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { CyflareAutocompleteComponent } from 'src/app/core/components/cyflare-autocomplete/cyflare-autocomplete.component';
import {
  IngestorService,
  IngestorTenantMap,
} from 'src/app/features/admin/organizations/components/integrations/services/ingestor.service';
import {
  csvFilename,
  CsvImportResult,
  extractTenantMapFieldErrors,
  IngestorTenantMapListResponse,
  IngestorTenantMapService,
  IngestorTenantMapWrite,
  isCsvImportResult,
  saveBlobAsFile,
  TenantMapFieldErrors,
  tenantNameLabel,
} from './services/ingestor-tenant-map.service';

/** DataTables renders render() output as HTML and does not escape, so escape free-text values. */
function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface TenantMapColumn {
  data: string | null;
  name: string;
  title: string;
  orderable?: boolean;
  render: (data: unknown, type: string, row: IngestorTenantMap) => string;
}

interface OrganizationOption {
  id: number;
  display_text: string;
}

type RecordState = 'loading' | 'ready' | 'missing' | 'error';

type CsvDownload = 'template' | 'export';

/** `rejected`: the backend answered 400 with row errors and wrote nothing. */
interface CsvUploadOutcome {
  result: CsvImportResult;
  dryRun: boolean;
  rejected: boolean;
}

const FORM_FIELDS = ['organization', 'integration_id', 'integration_unique_name'];

@Component({
  selector: 'app-ingestor-tenant-mapping',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputsModule,
    LabelModule,
    FileSelectModule,
    DatatableComponent,
    CyflareAutocompleteComponent,
  ],
  templateUrl: './ingestor-tenant-mapping.component.html',
  styleUrl: './ingestor-tenant-mapping.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [IngestorTenantMapService],
})
export class IngestorTenantMappingComponent {
  private readonly ingestorService = inject(IngestorService);
  private readonly tenantMapService = inject(IngestorTenantMapService);
  private readonly notificationService = inject(NotificationService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('datatable') datatable?: DatatableComponent;

  organizationId = input.required<number>();
  integrationId = input.required<number>();
  integrationName = input<string>('');
  tenantIdLabel = input<string>('External ID');

  close = output<void>();

  public readonly plusIcon: SVGIcon = plusIcon;
  public readonly xIcon: SVGIcon = xIcon;
  public readonly downloadIcon: SVGIcon = downloadIcon;
  public readonly fileCsvIcon: SVGIcon = fileCsvIcon;
  public readonly uploadIcon: SVGIcon = uploadIcon;
  public readonly csvRestrictions: FileRestrictions = { allowedExtensions: ['.csv'] };

  public recordState = signal<RecordState>('loading');
  public recordId = signal<number | null>(null);
  public canEdit = computed(() => this.recordState() === 'ready');
  public listUrl = computed(() => {
    const recordId = this.recordId();
    return recordId === null ? '' : this.tenantMapService.listUrl(recordId);
  });

  public idLabel = computed(() => this.tenantIdLabel().trim() || 'External ID');
  public nameLabel = computed(() => tenantNameLabel(this.idLabel()));
  public title = computed(() => `${this.integrationName().trim()} Tenant Mappings`.trim());
  public missingRecordMessage = computed(() => {
    const name = this.integrationName().trim();
    return `Save the ${name ? `${name} ` : ''}integration first. Tenant mappings can be added once it is saved.`;
  });

  public columns = computed<TenantMapColumn[]>(() => [
    {
      data: 'organization.name',
      name: 'organization__name',
      title: 'Organization',
      render: (data) => escapeHtml(data),
    },
    {
      data: 'integration_id',
      name: 'integration_id',
      title: this.idLabel(),
      render: (data) => escapeHtml(data),
    },
    {
      data: 'integration_unique_name',
      name: 'integration_unique_name',
      title: this.nameLabel(),
      render: (data) => escapeHtml(data),
    },
    {
      data: null,
      name: 'actions',
      title: 'Actions',
      orderable: false,
      render: (_data, _type, row) => this.renderActions(row),
    },
  ]);

  // Dialog state
  public dialogMode = signal<'add' | 'edit' | null>(null);
  public editing = signal<IngestorTenantMap | null>(null);
  public organizationDefault = signal<OrganizationOption | null>(null);
  public fieldErrors = signal<TenantMapFieldErrors>({});
  public generalErrors = computed(() =>
    Object.entries(this.fieldErrors())
      .filter(([field]) => !FORM_FIELDS.includes(field))
      .flatMap(([, messages]) => messages)
  );
  public saving = signal<boolean>(false);

  public deleting = signal<IngestorTenantMap | null>(null);
  public deleteBusy = signal<boolean>(false);

  public downloading = signal<CsvDownload | null>(null);

  public uploadOpen = signal<boolean>(false);
  public csvFile = signal<File | null>(null);
  public dryRun = signal<boolean>(false);
  public uploading = signal<boolean>(false);
  public uploadOutcome = signal<CsvUploadOutcome | null>(null);
  public uploadError = signal<string | null>(null);
  public csvColumns = computed(() =>
    ['organization_id', 'organization_name', this.idLabel(), this.nameLabel()].join(', ')
  );

  public form = this.fb.group({
    organization: this.fb.control<number | null>(null, Validators.required),
    integration_id: this.fb.nonNullable.control<string>('', [Validators.required, Validators.pattern(/\S/)]),
    integration_unique_name: this.fb.nonNullable.control<string>(''),
  });

  private rows = new Map<number, IngestorTenantMap>();

  constructor() {
    const target = computed(() => ({ organizationId: this.organizationId(), integrationId: this.integrationId() }));

    toObservable(target)
      .pipe(
        switchMap(({ organizationId, integrationId }) => {
          this.recordState.set('loading');
          this.recordId.set(null);
          return this.ingestorService.getIngestorRecordsByOrganizationv2(organizationId, integrationId).pipe(
            map((record) =>
              record ? { state: 'ready' as const, id: record.id } : { state: 'missing' as const, id: null }
            ),
            catchError(() => of({ state: 'error' as const, id: null }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ state, id }) => {
        this.recordId.set(id);
        this.recordState.set(state);
        if (state === 'error') {
          this.showNotification('Failed to load the integration record', 'error');
        }
      });
  }

  /** Datatable successCallback. Keeps the rows of the current page for the action buttons. */
  public onRowsLoaded = (response: IngestorTenantMapListResponse): void => {
    this.rows = new Map((response?.value ?? []).map((row) => [row.id, row]));
  };

  public onTableClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('button[data-action][data-id]');
    if (!button) return;

    const row = this.rows.get(Number(button.dataset['id']));
    if (!row) return;

    if (button.dataset['action'] === 'edit') {
      this.openEditDialog(row);
    } else if (button.dataset['action'] === 'delete') {
      this.openDeleteDialog(row);
    }
  }

  public openAddDialog(): void {
    if (!this.canEdit()) return;
    this.editing.set(null);
    this.organizationDefault.set(null);
    this.form.reset({ organization: null, integration_id: '', integration_unique_name: '' });
    this.fieldErrors.set({});
    this.dialogMode.set('add');
  }

  public openEditDialog(row: IngestorTenantMap): void {
    this.editing.set(row);
    this.organizationDefault.set({ id: row.organization.id, display_text: row.organization.name });
    this.form.reset({
      organization: row.organization.id,
      integration_id: row.integration_id ?? '',
      integration_unique_name: row.integration_unique_name ?? '',
    });
    this.fieldErrors.set({});
    this.dialogMode.set('edit');
  }

  public closeDialog(): void {
    this.dialogMode.set(null);
    this.editing.set(null);
    this.organizationDefault.set(null);
    this.fieldErrors.set({});
  }

  public onOrganizationSelected(option: OrganizationOption): void {
    if (!option) return;
    this.form.controls.organization.setValue(option.id);
    this.clearFieldError('organization');
  }

  public onOrganizationCleared(): void {
    this.form.controls.organization.setValue(null);
  }

  public clearFieldError(field: string): void {
    const errors = this.fieldErrors();
    if (!errors[field]) return;
    const { [field]: _removed, ...rest } = errors;
    this.fieldErrors.set(rest);
  }

  public errorsFor(field: string): string[] {
    return this.fieldErrors()[field] ?? [];
  }

  public saveMapping(): void {
    const recordId = this.recordId();
    const { organization, integration_id, integration_unique_name } = this.form.getRawValue();
    if (recordId === null || organization === null || this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }

    const editing = this.editing();
    const body: IngestorTenantMapWrite = {
      ...(editing ? { id: editing.id } : {}),
      organization,
      integration_id: integration_id.trim(),
      integration_unique_name: integration_unique_name.trim() || null,
    };

    this.saving.set(true);
    this.fieldErrors.set({});
    this.tenantMapService
      .upsert(recordId, body)
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.showNotification(editing ? 'Tenant mapping updated' : 'Tenant mapping created', 'success');
          this.closeDialog();
          this.reloadTable();
        },
        error: (error: HttpErrorResponse) => {
          const errors = error.status === 400 || error.status === 409 ? extractTenantMapFieldErrors(error) : {};
          if (Object.keys(errors).length) {
            this.fieldErrors.set(errors);
            return;
          }
          this.showNotification('Failed to save the tenant mapping', 'error');
        },
      });
  }

  public openDeleteDialog(row: IngestorTenantMap): void {
    this.deleting.set(row);
  }

  public closeDeleteDialog(): void {
    this.deleting.set(null);
  }

  public confirmDelete(): void {
    const row = this.deleting();
    if (!row || this.deleteBusy()) return;

    this.deleteBusy.set(true);
    this.tenantMapService
      .delete(row.id)
      .pipe(
        finalize(() => this.deleteBusy.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.showNotification('Tenant mapping deleted', 'success');
          this.closeDeleteDialog();
          this.reloadTable();
        },
        error: (error: HttpErrorResponse) => {
          const detail = extractTenantMapFieldErrors(error)['detail']?.[0];
          this.showNotification(detail ?? 'Failed to delete the tenant mapping', 'error');
        },
      });
  }

  public downloadCsv(kind: CsvDownload): void {
    const recordId = this.recordId();
    if (recordId === null || this.downloading()) return;

    const template = kind === 'template';
    this.downloading.set(kind);
    this.tenantMapService
      .downloadCsv(recordId, template)
      .pipe(
        finalize(() => this.downloading.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (blob) => saveBlobAsFile(blob, csvFilename(this.integrationName(), template)),
        error: () =>
          this.showNotification(
            template ? 'Failed to download the CSV template' : 'Failed to export the tenant mappings',
            'error'
          ),
      });
  }

  public openUploadDialog(): void {
    if (!this.canEdit()) return;
    this.csvFile.set(null);
    this.dryRun.set(false);
    this.uploadOutcome.set(null);
    this.uploadError.set(null);
    this.uploadOpen.set(true);
  }

  public closeUploadDialog(): void {
    this.uploadOpen.set(false);
  }

  public onCsvFileChange(files: File[] | null): void {
    this.csvFile.set(files?.[0] ?? null);
    this.uploadOutcome.set(null);
    this.uploadError.set(null);
  }

  public uploadCsv(): void {
    const recordId = this.recordId();
    const file = this.csvFile();
    if (recordId === null || !file || this.uploading()) return;

    const dryRun = this.dryRun();
    this.uploading.set(true);
    this.uploadOutcome.set(null);
    this.uploadError.set(null);
    this.tenantMapService
      .uploadCsv(recordId, file, dryRun)
      .pipe(
        finalize(() => this.uploading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result) => {
          this.uploadOutcome.set({ result, dryRun, rejected: false });
          if (!dryRun) {
            this.reloadTable();
          }
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 400 && isCsvImportResult(error.error)) {
            this.uploadOutcome.set({ result: error.error, dryRun, rejected: true });
            return;
          }
          const detail = extractTenantMapFieldErrors(error)['detail']?.[0];
          this.uploadError.set(detail ?? 'Failed to upload the CSV file.');
        },
      });
  }

  public closeMapping(): void {
    this.close.emit();
  }

  private reloadTable(): void {
    this.datatable?.reload();
  }

  private renderActions(row: IngestorTenantMap): string {
    const id = Number(row.id);
    return `
      <div class="tenant-map-actions">
        <button type="button" class="btn btn-sm btn-outline-primary" data-action="edit" data-id="${id}" title="Edit">Edit</button>
        <button type="button" class="btn btn-sm btn-outline-danger" data-action="delete" data-id="${id}" title="Delete">Delete</button>
      </div>
    `;
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      cssClass: `notification-${type}`,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true },
      closable: false,
      hideAfter: 3000,
    });
  }
}
